import type { ReactNode } from 'react'

import type { WorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'

import type { PageBreadcrumbItem } from '~/components/common/PageBreadcrumb'
import { PageLayout } from '~/components/common/PageLayout'
import { WorkflowBackBar } from '~/components/common/WorkflowBackBar'
import { WorkflowNotFoundView } from '~/components/common/WorkflowNotFoundView'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'

/**
 * `PageLayout` と同じく header xor headerExtra の型排他にする (両方渡して片方が黙って
 * 捨てられるのを防ぐ)。`breadcrumbItems` はどちらの形でも必須: `header` 形でも
 * ワークフローが見つからない時のヘッダーに使う (無効な ID の画面にも現在地は出す)。
 */
type Props = {
  children: ReactNode
  breadcrumbItems: PageBreadcrumbItem[]
  /** `WorkflowBackBar` の `guide` (この画面でやること)。 */
  backBarGuide?: (wfCase: WorkflowCase) => string
  /**
   * ページ最上部の通知 (Compact Alert 等)。`PageLayout` の `notices` として戻りバーの
   * **後ろ** に並べる (戻りバー → 通知。間隔 0、枠の端に寄せる。UI 辞書 alert-placement.md §0)。
   * ワークフローが見つからない時は出さない (戻りバーも本体と同様に出ない)。
   */
  notices?: ReactNode
} & (
  | {
      /** `PageLayout` の `headerExtra` (日付操作等)。ワークフローが見つからない時は出さない。 */
      headerExtra?: ReactNode
      header?: never
    }
  | {
      /**
       * `PageLayout` の完全カスタムヘッダー (スポット入札の日付ナビ等)。`breadcrumbItems`
       * の代わりに出す。ワークフローが見つからない時は出さない。
       */
      header: ReactNode
      headerExtra?: never
    }
)

/**
 * 業務ワークフローの遷移先画面 (`?workflowId=` で開く画面) の外枠。
 * `PageLayout` に `WorkflowBackBar` と AC-WFW-040 の差し替えを束ねる。
 *
 * workflowId のワークフローが見つからない時 (存在しない・他組織・形式不正、spec
 * AC-WFW-040) は、パンくずだけを残して header の日付操作と children を出さず、
 * `WorkflowNotFoundView` に差し替える。本体ごと差し替えるので、編集・確定操作も
 * 対象日に依存するデータも描画されない。
 *
 * `WorkflowBackBar` は `PageLayout` の通知の先頭に置き、出すかどうかは BackBar 自身の判定に任せる
 * (caseId 無し・見つからない時は BackBar が null を返す)。ここで重ねて隠さない。
 *
 * caseId は `useCurrentWorkflowCase` が URL から解決する (呼び出し側の hook と
 * query cache を共有するので fetch は重複しない)。本 layout を通らない早期 return
 * (全画面の `PlaceholderPage` 等) は差し替えの対象外なので、AC-WFW-040 を優先させたい
 * 時は呼び出し側で `isWorkflowNotFound` を見て本 layout に流す。
 */
export const WorkflowPageLayout = ({
  children,
  breadcrumbItems,
  headerExtra,
  header,
  backBarGuide,
  notices,
}: Props): React.ReactElement => {
  const { caseId, isWorkflowNotFound } = useCurrentWorkflowCase()
  const body = isWorkflowNotFound ? <WorkflowNotFoundView /> : children
  // 戻りバーは通知の先頭 (枠の端に寄せ、次の通知とは間隔 0)。戻りバーも通知も無い時 (caseId 無しで
  // notices 省略) は notices を渡さず、PageLayout は余分な枠を描画しない。
  // 戻りバーが null を返す時 (取得前の見つからない判定など) は PageNoticeFrame が空の通知領域を畳む。
  const pageNotices =
    caseId === undefined && notices === undefined ? undefined : (
      <>
        <WorkflowBackBar caseId={caseId} guide={backBarGuide} />
        {isWorkflowNotFound ? undefined : notices}
      </>
    )
  if (header !== undefined && !isWorkflowNotFound) {
    return (
      <PageLayout header={header} notices={pageNotices}>
        {body}
      </PageLayout>
    )
  }
  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      headerExtra={isWorkflowNotFound ? undefined : headerExtra}
      notices={pageNotices}
    >
      {body}
    </PageLayout>
  )
}
